/*
 * Creator video hero (sections/pb-creator-video-hero.liquid).
 *
 * - Sound toggle + progress bar for the autoplaying, muted video.
 * - CTA scrolls smoothly to the title block above the product grid.
 * - ?code=<value> → the matching "Kortingscode" block of the section: the code is
 *   shown in the CTA and put on the headless cart by the shared pb-creator-code.js
 *   (also used by PDP v4), which takes the other codes of the section off first.
 *
 * Docs: .cursor/references/creator-video-hero.md
 */
(function () {
  if (window.__pbCreatorVideoHero) return;
  window.__pbCreatorVideoHero = true;

  function readCodes(root) {
    try {
      const raw = root.querySelector('[data-pb-cvh-codes]')?.textContent;
      const list = raw ? JSON.parse(raw) : [];
      return list.filter((entry) => entry && entry.param && entry.code);
    } catch (error) {
      console.error('[PB creator hero] invalid code JSON', error);
      return [];
    }
  }

  function initCode(root) {
    const codes = readCodes(root);
    if (!codes.length) return;

    const match = window.PBCreatorCode?.match(codes, window.location.search);
    if (!match) return;

    // Mobile CTA and desktop title banner.
    root.querySelectorAll('[data-pb-cvh-code-line]').forEach((line) => {
      const pct = line.querySelector('[data-pb-cvh-code-pct]');
      const chip = line.querySelector('[data-pb-cvh-code]');
      if (!pct || !chip) return;
      pct.textContent = match.percentage ? `−${match.percentage}% met code` : 'Met code';
      chip.textContent = match.code;
      line.hidden = false;
    });

    window.PBCreatorCode.apply(match, codes);
  }

  // Space above the hero (top bar) and how far the floating unified nav reaches into it,
  // so the CSS can size the video to the first screen and keep the sound button visible.
  // On desktop the hero is hidden and the title block sits under the nav instead, so the nav
  // bottom is measured from the section; the title is also lined up with the product grid.
  function measure(root) {
    const hero = root.querySelector('.pb-cvh__hero');
    const heroShown = !!hero?.offsetParent;
    const ref = heroShown ? hero : root;
    const heroTop = ref.getBoundingClientRect().top + window.scrollY;
    if (heroShown) root.style.setProperty('--pb-cvh-offset', `${Math.round(heroTop)}px`);

    const grid = root.__pbCvhTile?.isConnected ? root.__pbCvhTile.parentElement : null;
    if (grid) {
      const rect = grid.getBoundingClientRect();
      root.style.setProperty('--pb-cvh-grid-left', `${Math.round(rect.left)}px`);
      root.style.setProperty(
        '--pb-cvh-grid-right',
        `${Math.round(document.documentElement.clientWidth - rect.right)}px`,
      );
    }

    // Where the fixed nav rests at scroll 0, whatever the current scroll: offsetTop, not a rect,
    // because the bar slides out of view with a transform (same as pb-liquids-desk.js), plus the
    // part of the top bar that is scrolled away, which updateFixedNavTop in
    // pb-unified-navigation.liquid takes off the nav's top.
    const floating = document.querySelector('.pb-unified-nav__fixed');
    if (!floating?.offsetHeight) return;
    const topBar = document.querySelector('[data-pb-unified-top-bar]')?.offsetHeight || 0;
    const restingBottom = floating.offsetTop + floating.offsetHeight + Math.min(window.scrollY, topBar);
    root.style.setProperty('--pb-cvh-nav-bottom', `${Math.max(0, Math.round(restingBottom - heroTop))}px`);
  }

  // Slow or metered connection: keep the photo.
  // navigator.connection is Chromium-only, so iOS Safari always gets the video.
  // Returns why the video is skipped (logged with ?pbdebug=1), or '' when it may load.
  function videoUnwanted() {
    const connection = navigator.connection;
    if (connection?.saveData) return 'data saver';
    if (/(^|-)2g$|^3g$/.test(connection?.effectiveType || '')) return `connection ${connection.effectiveType}`;
    // prefers-reduced-motion deliberately does not stop the video: the ad recognition is the
    // point of this page (team decision, Oct 2026). Only the CTA arrow nudge respects it (CSS).
    return '';
  }

  // `media` is the mobile hero or the desktop grid tile; both hold the same video layer.
  function initVideo(media) {
    if (!media || media.__pbCvhMedia) return;
    media.__pbCvhMedia = true;
    const tpl = media.querySelector('[data-pb-cvh-video-tpl]');
    const skip = tpl ? videoUnwanted() : '';
    // With a fallback photo the video is only inserted (and only then downloaded) when wanted.
    if (tpl && !skip) {
      // importNode adopts the nodes into this document; a plain clone of template content
      // stays owned by the template's inert document until insertion.
      const fragment = document.importNode(tpl.content, true);
      const inserted = fragment.querySelector('.pb-cvh__video');
      const poster = media.querySelector('.pb-cvh__poster');
      if (inserted) {
        // iOS only autoplays when the element is muted; set the property, not just the attribute.
        inserted.muted = true;
        if (poster) inserted.setAttribute('poster', poster.currentSrc || poster.src);
      }
      tpl.after(fragment);
    }
    if (skip) window.PBDebug?.log?.('[PB creator hero] video skipped:', skip);

    const video = media.querySelector('.pb-cvh__video');
    if (!video) return;
    media.classList.add('has-video');
    // With a photo, the video only fades in once its first frame plays (see the CSS).
    video.addEventListener('playing', () => media.classList.add('is-playing'), { once: true });
    // The autoplay attribute is not always honoured on an element inserted by script (iOS);
    // a muted play() is allowed. A refusal (Low Power Mode) just leaves the photo in place.
    if (tpl) {
      video.play()?.catch((error) => window.PBDebug?.log?.('[PB creator hero] autoplay refused:', error?.name));
    }

    const bar = media.querySelector('[data-pb-cvh-progress]');
    if (bar) {
      video.addEventListener('timeupdate', () => {
        if (video.duration) bar.style.width = `${(video.currentTime / video.duration) * 100}%`;
      });
    }

    const sound = media.querySelector('[data-pb-cvh-sound]');
    const label = media.querySelector('[data-pb-cvh-sound-label]');
    sound?.addEventListener('click', () => {
      video.muted = !video.muted;
      if (!video.muted) video.play().catch(() => {});
      sound.setAttribute('aria-pressed', String(!video.muted));
      if (label) label.textContent = video.muted ? sound.dataset.labelOff : sound.dataset.labelOn;
    });
  }

  // From 750px main-collection shows 4 columns: the video moves from the hero into the grid as
  // a tile the size of one product card (CSS hides the hero). The tile is the first item of the first product grid after
  // this section; without a grid the hero simply stays.
  const desktopMq = window.matchMedia('(min-width: 750px)');

  function findGrid(root) {
    let section = root.closest('.shopify-section');
    while ((section = section?.nextElementSibling)) {
      const grid = section.querySelector('.product-grid');
      if (grid) return grid;
    }
    return null;
  }

  function setActive(media, active) {
    const video = media?.querySelector('.pb-cvh__video');
    if (!video) return;
    if (active) video.play()?.catch(() => {});
    else video.pause();
  }

  function initLayout(root, signal) {
    const hero = root.querySelector('.pb-cvh__hero');
    const tpl = root.querySelector('[data-pb-cvh-tile-tpl]');
    let tile = null;

    const apply = () => {
      if (desktopMq.matches && tpl) {
        // A re-render of the collection section (theme editor) drops the tile: insert it again.
        if (!tile?.isConnected) {
          const grid = findGrid(root);
          if (grid) {
            grid.prepend(document.importNode(tpl.content, true));
            tile = grid.firstElementChild;
          }
        }
        root.__pbCvhTile = tile;
        root.classList.toggle('has-tile', !!tile?.isConnected);
        if (tile?.isConnected) {
          const media = tile.querySelector('.pb-cvh__media');
          initVideo(media);
          setActive(media, true);
          setActive(hero, false);
          measure(root);
          return;
        }
      }
      root.classList.remove('has-tile');
      initVideo(hero);
      setActive(hero, true);
      setActive(tile?.querySelector('.pb-cvh__media'), false);
      measure(root);
    };

    apply();
    desktopMq.addEventListener('change', apply, { signal });
    document.addEventListener('shopify:section:load', apply, { signal });
  }

  function initCta(root) {
    const cta = root.querySelector('[data-pb-cvh-cta]');
    cta?.addEventListener('click', (event) => {
      const target = document.getElementById((cta.getAttribute('href') || '').slice(1));
      if (!target) return;
      event.preventDefault();
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    });
  }

  function init(scope) {
    scope.querySelectorAll('[data-pb-cvh]').forEach((root) => {
      if (root.__pbCvhInit) return;
      root.__pbCvhInit = true;
      // Every listener of this instance goes with its section (theme editor reload or delete).
      const controller = new AbortController();
      const { signal } = controller;
      // The top bar and nav can still change height once fonts and late scripts load.
      window.addEventListener('load', () => measure(root), { once: true, signal });
      // Next frame: the nav updates its own top in a requestAnimationFrame on resize.
      window.addEventListener('resize', () => requestAnimationFrame(() => measure(root)), { signal });
      // The tile lives in another section (the collection grid), so it is removed explicitly.
      document.addEventListener(
        'shopify:section:unload',
        (event) => {
          if (!event.target.contains(root)) return;
          root.__pbCvhTile?.remove();
          controller.abort();
        },
        { signal },
      );
      initLayout(root, signal);
      initCta(root);
      initCode(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
